<template>
  <div class="h-calendar" :style="calendarVars">
    <div class="h-calendar__top">
      <div class="hc-switcher" ref="propSwitcher">
        <button
          type="button"
          class="hc-switcher__current"
          @click="togglePropList"
          :aria-expanded="isPropListOpen ? 'true' : 'false'"
          aria-haspopup="listbox"
          :disabled="isHotelReception"
        >
          <span class="hc-switcher__name">{{ currentPropertyName }}</span>
          <span class="hc-switcher__caret" :class="{ 'is-open': isPropListOpen }" aria-hidden="true"></span>
        </button>

        <div
          v-if="isPropListOpen"
          class="hc-switcher__popover"
          role="listbox"
          :aria-activedescendant="activePropCode"
        >
          <div class="hc-switcher__label">Select property</div>
          <ul class="hc-switcher__list">
            <li
              v-for="opt in propertiesOptions"
              :key="opt.code"
              class="hc-switcher__item"
              role="option"
              :aria-selected="opt.code === period.property ? 'true' : 'false'"
            >
              <button
                type="button"
                class="hc-switcher__btn"
                :disabled="opt.code === period.property"
                @click="onSelectProperty(opt)"
              >
                {{ opt.name }}
              </button>
            </li>
          </ul>
        </div>
      </div>
      <app-cells :indent="false">
        <template v-if="canSubscribe">
          <HMButton @click.native="openSubscription()">Subscribe</HMButton>
          <HMLinkButton
            :to="{ name: 'hotel-management-availability-subscriptions' }"
            background="linear-gradient(180deg, #D9DFE8 0%, #E4E8EF 100%)"
          >
            {{ isGeneralManager ? 'All subscriptions' : 'My subscriptions' }} ({{ subscriptionCount }})
          </HMLinkButton>
        </template>
        <HMButton
          v-if="unassignedRooms.length > 0"
          @click.native="onOpenUnassignedRoomsModal"
          background="linear-gradient(180deg, #FFCC85 0%, #FFC67B 100%)"
          color="#000"
          style="position: relative"
        >
          <span class="hc-dates__badge hc-dates__badge--unset">{{ unassignedRooms.length }}</span>
          Unassigned Rooms
        </HMButton>
        <TheHCSearch/>
        <HMReloadButton v-model="isReloading" @start="reloadCalendarData" :disabled="isLoading"/>
      </app-cells>
    </div>
    <div class="h-calendar__wrapper">
      <div class="h-calendar__sidebar" ref="sidebar">
        <div class="hc-sidebar">
          <div class="hc-sidebar__header">
            <div class="hc-plug">
              <button @click="$emit('open-legend')" type="button" class="hc-legend-button">
                <img src="@/assets/img/hm/legend.svg" alt="">
                Legend
              </button>
            </div>
            <div class="hc-controls">
              <button
                @click="toggleDatePicker"
                type="button"
                class="hc-ctrl hc-ctrl--icon"
                aria-label="Open date picker"
                :disabled="isLoading || isRefreshing"
              >
                <MainCalendarIcon/>
              </button>
              <button
                @click="onToday"
                type="button"
                class="hc-ctrl hc-ctrl--today"
                :disabled="isLoading || isRefreshing"
              >
                Today
              </button>
              <button
                v-if="isHotelReception || isGeneralManager || isHotelReservations || isSalesManager"
                @click="toggleCreateMenu"
                type="button"
                class="hc-ctrl hc-ctrl--plus"
                aria-label="Create"
                :disabled="isLoading || isRefreshing"
              >
                <PlusIcon/>
              </button>
            </div>

            <div v-if="isCreateMenuOpen" ref="createPopover" class="hc-create-popover" role="menu">
              <ul class="hc-create">
                <li class="hc-create__item">
                  <button type="button" class="hc-create__btn" @click="onCreateNewReservation">
                    <PlusSmall class="hc-create__icon" aria-hidden="true"/>
                    <span>New Reservation</span>
                  </button>
                </li>
                <li class="hc-create__item">
                  <button type="button" class="hc-create__btn" @click="onBlockDates">
                    <BlockSmall class="hc-create__icon" aria-hidden="true"/>
                    <span>Block Dates</span>
                  </button>
                </li>
              </ul>
            </div>

            <div v-if="isDatePickerOpen" ref="datePopover" class="hc-date-popover">
              <date-picker
                @input="onDatePicked"
                :value="baseDate"
                :key="datePickerKey"
                :first-day-of-week="2"
                :masks="{ title: 'MMM YYYY', weekdays: 'WW' }"
                :weekday-names="['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']"
                :attributes="datePickerAttrs"
                locale="en-US"
                is-inline
              />
            </div>
          </div>

          <div class="hc-sidebar__list">
            <template v-if="!hasLoadedOnce">
              <div class="hc-skeleton">
                <div class="hc-sidebar__group">
                  <div class="hc-sidebar__title">&nbsp;</div>
                  <div class="hc-sidebar__rooms">
                    <div v-for="i in skeletonArray" :key="'sk_sb_room_' + i" class="hc-sidebar__room">&nbsp;</div>
                  </div>
                </div>
              </div>
            </template>

            <template v-else>
              <div v-for="item in roomTypes" :key="item.name" class="hc-sidebar__group">
                <div class="hc-sidebar__title" :class="{ 'hc-sidebar__title--subscribable': canSubscribe }" :title="item.name">
                  {{ item.name }}
                  <button
                    v-if="canSubscribe"
                    type="button"
                    class="hc-subscribe"
                    title="Subscribe to this room type"
                    :aria-label="`Subscribe to ${item.name}`"
                    @click.stop="openSubscription(item.id)"
                  >🔔</button>
                </div>
                <div class="hc-sidebar__rooms">
                  <div
                    v-for="room in item.rooms"
                    :key="`room_${room.id}`"
                    class="hc-sidebar__room"
                    :title="room.room_name"
                    :class="{ 'is-selected': isRowSelected(room.id) }"
                    @click.stop="toggleRow(room.id)"
                  >
                    {{ room.room_name }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="h-calendar__body" ref="cal" v-drag-scroll>
        <div class="hc-head">
          <div class="hc-months">
            <div class="hc-months__bar" :style="monthsBarStyle">
              <div
                v-for="m in monthSegments"
                :key="m.label + '_' + m.startIdx"
                class="hc-months__item"
                :style="monthItemStyle(m)"
              >
                {{ m.label }}
              </div>
            </div>
          </div>

          <div class="hc-dates" :style="gridStyle">
            <div
              @click="onDateClick(d.idx)"
              v-for="d in dates"
              :key="d.iso"
              class="hc-dates__cell"
              :class="{ 'is-today': isToday(d.iso), 'is-selected': isColSelected(d.idx), 'has-unassigned': (occupancyByIso[d.iso]?.unassigned || 0) > 0 }"
              :title="d.iso"
            >
              <span v-if="(occupancyByIso[d.iso]?.unassigned || 0) > 0" class="hc-dates__badge">
                {{ occupancyByIso[d.iso].unassigned }}
              </span>
              <div class="hc-dates__dow">{{ d.dow }}</div>
              <div class="hc-dates__day">{{ d.day }}</div>
              <div class="hc-dates__occ" v-if="!isOccupancyLoading && occupancyByIso[d.iso]">
                {{ formatOcc(occupancyByIso[d.iso].occupancy) }}
              </div>
              <div v-else class="hc-oc-skeleton"></div>
            </div>
          </div>
        </div>

        <div v-if="!hasLoadedOnce" class="hc-skeleton">
          <div class="hc-availability">
            <div class="hc-availability__grid" :style="gridStyle">
              <div v-for="d in dates" :key="'sk_av_' + d.iso" class="hc-availability__cell"
                   :class="{ 'is-today': isToday(d.iso) }"></div>
            </div>
          </div>

          <div class="hc-rooms">
            <div v-for="i in skeletonArray" :key="'sk_row_' + i" class="hc-row">
              <div class="hc-row__grid" :style="gridStyle">
                <div v-for="d in dates" :key="'sk_cell_' + i + '_' + d.iso" class="hc-row__cell"
                     :class="{ 'is-today': isToday(d.iso) }"></div>
              </div>
            </div>
          </div>
        </div>

        <div v-for="group in roomTypes" :key="'g_'+group.id" class="hc-section">
          <div class="hc-availability">
            <div class="hc-availability__grid" :style="gridStyle">
              <div
                v-for="d in dates"
                :key="'a_'+group.id+'_'+d.iso"
                class="hc-availability__cell"
                :class="{ 'is-today': isToday(d.iso), 'is-selected': isColSelected(d.idx) }"
              >
                <template v-if="!isAvailabilityLoading">
                  <span
                    class="hc-availability__val"
                    :class="{ 'hc-availability__val--zero': getAvailability(group.id, d.iso) === 0 }"
                  >
                    {{ getAvailability(group.id, d.iso) }}
                  </span>
                </template>
                <template v-else>
                  <span class="hc-av-skeleton"></span>
                </template>
              </div>
            </div>
          </div>

          <div class="hc-rooms">
            <div
              v-for="room in group.rooms"
              :key="'r_'+room.id"
              class="hc-row"
              :data-room-id="room.id"
              @dragover.prevent="onRowDragOver(room.id, $event)"
              @dragenter.prevent="onRowDragEnter(room.id, $event)"
              @dragleave="onRowDragLeave(room.id, $event)"
              @drop.prevent="onRowDrop(room.id, $event)"
            >
              <div class="hc-row__grid" :style="gridStyle">
                <div
                  v-for="d in dates"
                  :key="'c_'+room.id+'_'+d.iso"
                  class="hc-row__cell"
                  :class="{ 'is-today': isToday(d.iso), 'is-selected': isColSelected(d.idx) || isRowSelected(room.id) }"
                  :style="cellDropStyle(room.id, d.idx)"
                ></div>
                <div
                  v-for="b in bookingsByRoom[room.id] || []"
                  :key="`b_${b._source}_${b.id}`"
                  class="hc-booking"
                  :class="[
                    bookingToneClasses(b),
                    {
                      'hc-booking--cut-left': isCutLeft(b),
                      'hc-booking--cut-right': isCutRight(b),
                      'is-loading': isBookingLoading(b),
                      'is-disabled': b._source === 'block' && !isGeneralManager
                    }
                  ]"
                  :style="bookingStyle(b)"
                  :title="`${b.name} · ${b.from_date} → ${b.to_date}`"
                  :aria-label="`${b.name}, ${b.from_date} → ${b.to_date}`"
                  role="button"
                  tabindex="0"
                  @click.stop="onBookingClick(b, $event)"
                  @mousedown.stop
                  @keydown.enter.prevent="onBookingClick(b, $event)"
                  @keydown.space.prevent="onBookingClick(b, $event)"
                  :draggable="canDrag(b)"
                  @dragstart="onBookingDragStart(b, $event)"
                  @dragend="onBookingDragEnd"
                >
                  <span class="hc-booking__name">{{ b.name }}</span>
                  <span class="hc-booking__icons">
                    <LockerIcon v-if="b._source === 'reservation' && b.lock_assignment"
                                class="hc-booking__icon hc-booking__lock"/>
                   <DotIcon
                     v-if="(b._source === 'reservation' || b._source === 'shared') && b.outstanding_balance"
                     class="hc-booking__icon hc-booking__dot"
                   />
                    <span v-if="isBookingLoading(b)" class="hc-booking__spinner"></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isLoading || isRefreshing" class="hc-refresh-mask">
          <div class="hc-refresh-spinner" aria-hidden="true"></div>
        </div>
      </div>
    </div>

    <div v-if="popoverShow" class="hc-popover" ref="popover" :style="popoverStyle">
      <div
        class="hc-popover__tail"
        :class="`hc-popover__tail--${tailSide}`"
        :style="{ left: `${tailLeft}px` }"
      ></div>
      <div class="hc-popover__body">
        <button @click="popoverShow = false" class="hc-popover__close" type="button">
          <CloseSearch/>
        </button>
        <div class="hc-popover__wrapper">
          <div class="hc-popover__top">
            <div class="hc-popover__title">{{ popoverData.reservation.group_name }}</div>
            <HMStatus
              :status="popoverData.status"
              @select="opt => onSelectStatus(opt, popoverData.id, popoverData.status.code)"
            />
          </div>
          <template v-if="popoverData.guests && popoverData.guests.length > 0">
            <div v-for="guest in popoverData.guests" :key="guest.id" class="guest">
            <span class="guest__gender">
              <MaleIcon v-if="guest.gender && guest.gender.code === 'male'"/>
              <FemaleIcon v-else-if="guest.gender && guest.gender.code === 'female'"/>
            </span>
              <span
                :class="{ 'guest__flag--empty': !(guest.citizenship && guest.citizenship.code) }"
                :title="guest.citizenship && guest.citizenship.name ? guest.citizenship.name : ''"
                class="guest__flag"
              >
              <span v-if="guest.citizenship && guest.citizenship.code" :class="`fflag ff-md fflag-${guest.citizenship.code}`"></span>
            </span>
              <span class="guest__name">
              <span>
                {{ (guest.gender && guest.gender.greeting) ? guest.gender.greeting : ''
                }} {{ guest.full_name || ((guest.first_name || '') + ' ' + (guest.last_name || '')) }}
              </span>
            </span>
            </div>
          </template>
          <div class="hc-popover__room">
            <div class="hc-popover__room-top hc-popover__row">
              <div class="hc-popover__name">
                <span>{{ popoverData.room.short_name || '' }}</span>&nbsp;
                <span>{{ popoverData.short_room.room_category.name }}</span>
              </div>
              <div class="hc-popover__balance">
                <span>Balance:</span>
                <HMBalance :balance="popoverData.balance"/>
              </div>
            </div>
            <div class="hc-popover__room-main hc-popover__row">
              <div class="hc-popover__col hc-popover__col--dates">
                <div class="hc-popover__tower">
                  <div class="hc-popover__tower-title">Check In:</div>
                  <div class="hc-popover__tower-body">
                    <CalendarIcon/>
                    {{ formatDate(popoverData.short_room.check_in) }}
                  </div>
                </div>
                <div class="hc-popover__arrow">
                  <ArrowIcon/>
                </div>
                <div class="hc-popover__tower">
                  <div class="hc-popover__tower-title">Check Out:</div>
                  <div class="hc-popover__tower-body">
                    <CalendarIcon/>
                    {{ formatDate(popoverData.short_room.check_out) }}
                  </div>
                </div>
              </div>
              <div class="hc-popover__col">
                <div class="hc-popover__tower">
                  <div class="hc-popover__tower-title">Nights:</div>
                  <div class="hc-popover__tower-body">
                    <HMChip>
                      {{ popoverData.short_room.nights || '0' }}
                      <template #icon>
                        <NightsIcon aria-hidden="true"/>
                      </template>
                    </HMChip>
                  </div>
                </div>
                <div class="hc-popover__tower">
                  <div class="hc-popover__tower-title">Guests:</div>
                  <div class="hc-popover__tower-body">
                    <HMChip v-if="popoverData.short_room.accomodation_type?.age_assignment?.adult" color="#9AB992">
                      {{ popoverData.short_room.accomodation_type.age_assignment.adult }}
                      <template #icon>
                        <AdultIcon aria-hidden="true"/>
                      </template>
                    </HMChip>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="hc-popover__meta">
            {{ popoverData.reservation.source.name }}
            {{ popoverData.reservation.external_id ? `(${ popoverData.reservation.external_id })` : '' }}
          </div>
          <app-cells :indent="false" position="between">
            <app-cells :indent="false" gap="10px">
              <button
                v-if="!popoverData.lock_assignment && !!popoverData.room"
                @click="onLock(popoverData)"
                type="button"
                class="hc-popover__action-button"
                :disabled="btnLoading.lock"
              >
                <HMTooltip>
                  <template #trigger>
                    <span v-if="btnLoading.lock" class="hc-btn-spinner hc-btn-spinner--lock"></span>
                    <img v-else src="@/assets/img/hm/lock-square.svg" alt="">
                  </template>
                  <template #content>Lock</template>
                </HMTooltip>
              </button>
              <button
                v-if="!popoverData.lock_assignment"
                @click="onChange(popoverData)"
                type="button"
                class="hc-popover__action-button"
              >
                <HMTooltip>
                  <template #trigger>
                    <img src="@/assets/img/hm/change-square.svg" alt="">
                  </template>
                  <template #content>Change</template>
                </HMTooltip>
              </button>
              <button
                v-if="!popoverData.lock_assignment && !!popoverData.room && popoverData.status.code !== 'checked_in'"
                @click="onUnassign(popoverData)"
                type="button"
                class="hc-popover__action-button"
                :disabled="btnLoading.unassign"
              >
                <HMTooltip>
                  <template #trigger>
                    <span v-if="btnLoading.unassign" class="hc-btn-spinner hc-btn-spinner--unassign"></span>
                    <img v-else src="@/assets/img/hm/unassign-square.svg" alt="">
                  </template>
                  <template #content>Unassign</template>
                </HMTooltip>
              </button>
              <button
                v-if="popoverData.lock_assignment"
                @click="onUnlock(popoverData)"
                type="button"
                class="hc-popover__action-button"
                :disabled="btnLoading.unlock"
              >
                <HMTooltip>
                  <template #trigger>
                    <span v-if="btnLoading.unlock" class="hc-btn-spinner hc-btn-spinner--unlock"></span>
                    <img v-else src="@/assets/img/hm/unlock-square.svg" alt="">
                  </template>
                  <template #content>Unlock</template>
                </HMTooltip>
              </button>
            </app-cells>
            <app-cells :indent="false">
              <HMLinkButton
                :to="{
                name: 'hotel-management-reservations-detail-rooms-detail',
                params: { id: popoverData.reservation.id, roomId: popoverData.id }
              }"
                :background="'linear-gradient(180deg, #D9DFE8 0%, #E4E8EF 100%)'"
              >
                Folio
              </HMLinkButton>
              <HMLinkButton
                :to="{
                name: 'hotel-management-reservations-detail-reservation',
                params: { id: popoverData.reservation.id }
              }"
              >
                Details
              </HMLinkButton>
            </app-cells>
          </app-cells>
        </div>
      </div>
    </div>
    <div v-if="popoverSharedShow" class="hc-popover" ref="popover" :style="popoverStyle">
      <div class="hc-popover__tail" :class="`hc-popover__tail--${tailSide}`" :style="{ left: `${tailLeft}px` }"></div>
      <div class="hc-popover__body">
        <button @click="popoverSharedShow = false" class="hc-popover__close" type="button">
          <CloseSearch/>
        </button>
        <div class="hc-popover__shared-title">
          Shared Accomodation (Room {{ popoverData.room.short_name }})
        </div>
        <div class="hc-popover__wrapper">
          <HMCard margin-bottom="20px">
            <app-cells :indent="false" position="between">
              <HMDates
                title-first="Check in:"
                :date-first="popoverData.check_in"
                title-second="Check out:"
                :date-second="popoverData.check_out"
              />
              <app-cells :indent="false" gap="20px">
                <HMTower v-if="popoverData.nights">
                  <template #title>Nights:</template>
                  <template #body>
                    <HMChip>
                      {{ popoverData.nights }}
                      <template #icon>
                        <NightsIcon aria-hidden="true"/>
                      </template>
                    </HMChip>
                  </template>
                </HMTower>
                <HMTower v-if="popoverData.accomodation_type?.age_assignment?.adult">
                  <template #title>Guests:</template>
                  <template #body>
                    <HMChip color="#9AB992">
                      {{ popoverData.accomodation_type.age_assignment.adult }}
                      <template #icon>
                        <AdultIcon aria-hidden="true"/>
                      </template>
                    </HMChip>
                  </template>
                </HMTower>
              </app-cells>
            </app-cells>
          </HMCard>
          <div class="hc-popover__meta">GUESTS:</div>
          <HMCard v-for="(room, index) in popoverData.reservations" :key="room.id" margin-bottom="10px">
            <app-cells :indent="false" position="between" margin-bottom="15px">
              <div class="hc-popover__title">{{ index + 1 }}. {{ room.reservation.group_name }}</div>
              <HMStatus
                :status="room.status"
                @select="opt => onSelectStatus(opt, room.id, room.status.code)"
              />
            </app-cells>
            <template v-if="room.guests && room.guests.length > 0">
              <div v-for="guest in room.guests" :key="guest.id" class="guest">
                <span class="guest__gender">
                  <MaleIcon v-if="guest.gender && guest.gender.code === 'male'"/>
                  <FemaleIcon v-else-if="guest.gender && guest.gender.code === 'female'"/>
                </span>
                <span
                  :class="{ 'guest__flag--empty': !(guest.citizenship && guest.citizenship.code) }"
                  :title="guest.citizenship && guest.citizenship.name ? guest.citizenship.name : ''"
                  class="guest__flag"
                >
                  <span v-if="guest.citizenship && guest.citizenship.code" :class="`fflag ff-md fflag-${guest.citizenship.code}`"></span>
                </span>
                <span class="guest__name">
                  <span>
                    {{ (guest.gender && guest.gender.greeting) ? guest.gender.greeting : ''
                    }} {{ guest.full_name || ((guest.first_name || '') + ' ' + (guest.last_name || '')) }}
                  </span>
                </span>
              </div>
            </template>
            <div class="hc-popover__meta">
              {{ room.reservation.source.name }}
              {{ room.reservation.external_id ? `(${ room.reservation.external_id })` : '' }}
            </div>
            <app-cells :indent="false" position="between">
              <HMTower horizontal>
                <template #title>Balance:</template>
                <template #body>
                  <HMBalance :balance="room.balance"/>
                </template>
              </HMTower>
              <app-cells :indent="false" gap="15px">
                <HMLinkButton
                  :to="{
                    name: 'hotel-management-reservations-detail-rooms-detail',
                    params: { id: room.reservation.id, roomId: room.id }
                    }"
                  :background="'linear-gradient(180deg, #D9DFE8 0%, #E4E8EF 100%)'"
                >
                  Folio
                </HMLinkButton>
                <HMLinkButton
                  :to="{ name: 'hotel-management-reservations-detail-reservation', params: { id: room.reservation.id } }"
                >
                  Details
                </HMLinkButton>
              </app-cells>
            </app-cells>
          </HMCard>
        </div>
      </div>
    </div>
    <div v-if="popoverBlockShow" class="hc-popover" ref="popover" :style="popoverStyle">
      <div class="hc-popover__tail" :class="`hc-popover__tail--${tailSide}`" :style="{ left: `${tailLeft}px` }"></div>
      <div class="hc-popover__body">
        <button @click="popoverBlockShow = false" class="hc-popover__close" type="button">
          <CloseSearch/>
        </button>
        <div class="hc-popover__wrapper">
          <div class="hc-popover__top">
            <div class="hc-popover__title">{{ popoverData.name }}</div>
          </div>
          <div v-if="blockMeta" class="hc-popover__meta">{{ blockMeta }}</div>

          <app-cells :indent="false" margin-top="20px" position="end">
            <HMButton @click.native="onDeleteBlock" :loading="isSubmittingDeleteBlock">
              Delete
            </HMButton>
          </app-cells>
        </div>
      </div>
    </div>
    <modal
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      :width="300"
      name="change-room"
      class="modal-not-overflow"
    >
      <div class="popup-new">
        <button class="popup-new__close" type="button" @click="$modal.hide('change-room')"></button>
        <div class="popup-new__title popup-new__title--mb-15">Change Room</div>
        <div class="popup-new__description">
          <template v-if="current_room && current_room.room && rooms_available.length > 0">
            {{ current_room.room.short_name || '' }} {{ current_room.short_room.room_category.name }}
          </template>
          <app-cells v-else :indent="false" gap="10px">
            <img src="@/assets/img/hm/warning.svg" alt=""> Unassigned
          </app-cells>
        </div>
        <div class="popup-new__body">
          <app-form-field label="Change to:" class="family">
            <app-select
              v-model="room_to_change"
              :reduce="item => item.id"
              :options="rooms_available"
              label="room_name"
              placeholder="Choose room"
              class="family"
            />
          </app-form-field>
          <app-cells :indent="false" margin-top="20px" position="end">
            <HMButton
              @click.native="onChangeRoom"
              :loading="isSubmittingChangeRoom"
              :disabled="!room_to_change"
            >
              Change Room
            </HMButton>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      :width="790"
      name="unassigned-rooms"
      class="modal-not-overflow"
    >
      <div class="popup-new">
        <button class="popup-new__close" type="button" @click="$modal.hide('unassigned-rooms')"></button>
        <div class="popup-new__title popup-new__title--mb-15">Unassigned Rooms | {{ propertyObject.name }}</div>
        <div class="popup-new__body">
          <HotelUnassignedRoom
            v-for="(room, index) in unassignedRooms"
            :key="`unassigned_${room.id}`"
            @room-patched="onUnassignedRoomPatched"
            :order-number="index + 1"
            :room="room"
          />
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getAvailabilitySubscriptions,
  getHotelManagementCalendarReservationRooms,
  getHotelManagementCalendarRoomTypeAvailability,
  getHotelManagementCalendarRoomTypes,
  getHotelManagementCalendarBlocks,
  getHotelManagementPropertiesV2,
  getHotelManagementCalendarOccupancy,
  getHotelManagementReservationsRoom,
  getHotelManagementCalendarSharedRooms,
  patchHotelManagementReservationsRooms,
  getHotelManagementReservationShareableRoomsList,
  getHotelManagementRoomsAvailable,
  getHotelManagementReservationsRooms,
  patchHotelManagementBlocks,
  getHotelManagementRoomCategories,
  getHotelManagementBlock,
  deleteHotelManagementBlock,
} from '@/http'
import { addDays, format, parseISO, subDays, differenceInCalendarDays, endOfMonth } from 'date-fns'
import MainCalendarIcon from '@/views/hotel-management/calendar/icons/CalendarIcon.vue'
import CalendarIcon from '@/views/hotel-management/reservations/detail/icons/CalendarIcon.vue'
import PlusIcon from '@/views/hotel-management/calendar/icons/PlusIcon.vue'
import LockerIcon from '@/views/hotel-management/calendar/icons/LockerIcon.vue'
import DotIcon from '@/views/hotel-management/calendar/icons/DotIcon.vue'
import { DatePicker } from 'v-calendar'
import PlusSmall from '@/views/hotel-management/calendar/icons/PlusSmall.vue'
import BlockSmall from '@/views/hotel-management/calendar/icons/BlockSmall.vue'
import TheHCSearch from '@/views/hotel-management/calendar/TheHCSearch.vue'
import CloseSearch from '@/views/hotel-management/calendar/icons/CloseSearch.vue'
import HMBalance from '@/views/hotel-management/reservations/components/HMBalance.vue'
import ArrowIcon from '@/views/hotel-management/reservations/detail/icons/ArrowIcon.vue'
import NightsIcon from '@/views/hotel-management/reservations/detail/icons/NightsIcon.vue'
import HMChip from '@/views/hotel-management/reservations/components/HMChip.vue'
import AdultIcon from '@/views/hotel-management/reservations/detail/icons/AdultIcon.vue'
import HMStatus from '@/components/HMStatus.vue'
import HMLinkButton from '@/components/HMLinkButton.vue'
import HMCard from '@/components/HMCard.vue'
import HMDates from '@/components/HMDates.vue'
import HMTower from '@/components/HMTower.vue'
import HMReloadButton from '@/components/HMReloadSpinner.vue'
import HMButton from '@/views/hotel-management/reservations/components/HMButton.vue'
import HotelUnassignedRoom from '@/components/HotelUnassignedRoom.vue'
import HMTooltip from '@/views/hotel-management/reservations/components/HMTooltip.vue'
import MaleIcon from '@/views/hotel-management/reservations/detail/icons/MaleIcon.vue'
import FemaleIcon from '@/views/hotel-management/reservations/detail/icons/FemaleIcon.vue'

const COL_PX = 100
const ROW_PX = 38
const GAP_PX = 4
const BOOKING_VPAD = 4

const TONES = {
  default: ['hc-booking--tone-default'],
  blue: ['hc-booking--tone-blue'],
  turq: ['hc-booking--tone-turq'],
  green: ['hc-booking--tone-green'],
  black: ['hc-booking--tone-black'],
  gray: ['hc-booking--tone-gray'],
  red: ['hc-booking--tone-red'],
  yellow: ['hc-booking--tone-yellow'],
  burgundy: ['hc-booking--tone-burgundy'],
}

export default {
  name: 'TheHotelCalendar',
  components: {
    FemaleIcon,
    MaleIcon,
    HMTooltip,
    HotelUnassignedRoom,
    HMButton,
    HMReloadButton,
    HMTower,
    HMDates,
    HMCard,
    AdultIcon,
    HMChip,
    NightsIcon,
    ArrowIcon,
    HMBalance,
    CloseSearch,
    BlockSmall,
    PlusSmall,
    PlusIcon,
    CalendarIcon,
    DatePicker,
    LockerIcon,
    DotIcon,
    TheHCSearch,
    MainCalendarIcon,
    HMStatus,
    HMLinkButton
  },
  data() {
    return {
      isCoreLoading: true,
      isStatsLoading: false,
      isUnassignedLoading: false,
      isOccupancyLoading: false,
      isAvailabilityLoading: false,
      hasLoadedOnce: false,
      skeletonRows: 50,
      period: {
        from_date: '', to_date: '', property: 'aishi'
      },
      roomTypes: [],
      propertiesOptions: [],
      availabilities: [],
      reservations: [],
      blocks: [],
      sharedRooms: [],
      selectedColIdxs: [],
      selectedRoomIds: [],
      dragJustMoved: false,
      baseDate: new Date(),
      lastPickedDate: null,
      isDatePickerOpen: false,
      isPropListOpen: false,
      occupancy: [],
      isCreateMenuOpen: false,
      popoverData: {},
      popoverShow: false,
      popoverSharedShow: false,
      popoverPos: { top: 0, left: 0, minWidth: 100 },
      bookingLoadingKey: null,
      anchorEl: null,
      anchorRect: null,
      tailSide: 'top',
      tailLeft: 0,
      isReloading: false,
      subscriptionCount: 0,
      current_room: {},
      rooms_available: [],
      room_to_change: null,
      isSubmittingChangeRoom: false,
      dnd: {
        dragging: null,
        allowedByRoom: {},
        overRoomId: null,
      },
      unassignedRooms: [],
      btnLoading: {
        lock: false,
        unlock: false,
        unassign: false,
      },
      roomCategories: [],
      categoryAllowedGroups: {},
      popoverBlockShow: false,
      isSubmittingDeleteBlock: false,
    }
  },
  computed: {
    canSubscribe() {
      const roles = JSON.parse(localStorage.getItem('userGroups') || '[]')
      return ['aishi', 'brubru'].includes(this.propertyCode) && roles.some(role => ['general_manager', 'sales_manager', 'sales_supervisor'].includes(role.code))
    },
    propertyObject() {
      const code = this.propertyCode
      const fromList = (this.propertiesOptions || []).find(p => p.code === code)
      if (fromList) return fromList

      if (this.isHotelReception && this.userAppData && this.userAppData.hotel_property) {
        const hp = this.userAppData.hotel_property
        if (typeof hp === 'string') {
          if (hp === code) return { code: hp, name: hp }
        } else if (hp && hp.code === code) {
          return { code: hp.code, name: hp.name }
        }
      }

      return { code, name: code }
    },
    roomIdToGroupId() {
      const map = {}

      this.roomTypes.forEach(type => {
        type.rooms.forEach(room => {
          map[room.id] = type.id
        })
      })
      return map
    },
    popoverStyle() {
      return { top: `${ this.popoverPos.top }px`, left: `${ this.popoverPos.left }px`, minWidth: `${ this.popoverPos.minWidth }px` }
    },
    isLoading() {
      return this.isCoreLoading
    },
    isRefreshing() {
      return this.isCoreLoading && this.hasLoadedOnce
    },
    occupancyByIso() {
      const map = {}

      this.occupancy.forEach(o => {
        map[o.date] = o
      })
      return map
    },
    appSidebarWidth() {
      return this.$store.state.isBigSidebar ? 235 : 75
    },
    blockMeta() {
      const d = this.popoverData || {}
      const createdAt = d.created_at
      const creator =
        d.created_by && (d.created_by.full_name || d.created_by.name)

      if (!createdAt && !creator) return ''

      let when = ''
      if (createdAt) {
        try {
          const dt = typeof createdAt === 'string' ? parseISO(createdAt) : createdAt
          when = format(dt, 'dd MMM yyyy HH:mm')
        } catch (e) {
          when = ''
        }
      }

      if (when && creator) return `${when} (${creator})`
      if (when) return when
      if (creator) return `(${creator})`
      return ''
    },
    calendarVars() {
      return {
        '--app-sidebar-w': `${ this.appSidebarWidth }px`,
        '--col-px': `${ COL_PX }px`,
        '--row-px': `${ ROW_PX }px`,
      }
    },
    propertyCode() {
      return this.period.property
    },
    currentPropertyName() {
      const code = this.propertyCode
      const m = (this.propertiesOptions || []).find(x => x.code === code)
      if (m) return m.name

      if (this.isHotelReception && this.userAppData && this.userAppData.hotel_property) {
        const hp = this.userAppData.hotel_property
        const hpCode = typeof hp === 'string' ? hp : hp.code
        const hpName = typeof hp === 'string' ? hp : hp.name
        if (hpCode === code && hpName) return hpName
      }

      return code || ''
    },
    activePropCode() {
      return this.propertyCode
    },
    combinedBookings() {
      const res = this.reservations.map(r => ({ ...r, _source: 'reservation' }))
      const bl = this.blocks.map(b => ({ ...b, _source: 'block' }))
      const sh = this.sharedRooms.map(s => ({ ...s, _source: 'shared' }))

      return res.concat(bl, sh)
    },
    bookingsByRoom() {
      if (!this.period.from_date || !this.period.to_date) return {}

      const startDate = parseISO(this.period.from_date)
      const byRoom = {}

      this.combinedBookings.forEach(b => {
        const startIdx = Math.max(0, differenceInCalendarDays(parseISO(b.from_date), startDate))
        const endIdx = differenceInCalendarDays(parseISO(b.to_date), startDate)

        if (!byRoom[b.room_id]) byRoom[b.room_id] = []
        byRoom[b.room_id].push({ ...b, _startIdx: startIdx, _endIdx: endIdx })
      })
      Object.keys(byRoom).forEach(k => byRoom[k].sort((a, c) => a._startIdx - c._startIdx))

      return byRoom
    },
    skeletonArray() {
      return Array.from({ length: this.skeletonRows }, (_, i) => i)
    },
    dates() {
      const start = parseISO(this.period.from_date)
      const endInc = addDays(parseISO(this.period.to_date), 1)
      const days = differenceInCalendarDays(endInc, start)
      const out = []

      for (let i = 0; i < days; i++) {
        const d = addDays(start, i)
        out.push({ iso: format(d, 'yyyy-MM-dd'), day: format(d, 'd'), dow: format(d, 'EEE').toUpperCase(), idx: i })
      }

      return out
    },
    gridStyle() {
      return { gridTemplateColumns: `repeat(${ this.dates.length }, var(--col-px))` }
    },
    monthSegments() {
      if (!this.dates.length) return []

      let cur = parseISO(this.period.from_date)
      const endInc = addDays(parseISO(this.period.to_date), 1)
      const segs = []
      let startIdx = 0

      while (cur < endInc) {
        const endOfCurMonth = endOfMonth(cur)
        let segEnd = addDays(endOfCurMonth, 1)
        if (segEnd > endInc) segEnd = endInc
        const days = differenceInCalendarDays(segEnd, cur)

        segs.push({ label: format(cur, 'MMM yyyy').toUpperCase(), startIdx, days })
        startIdx += days
        cur = segEnd
      }
      return segs
    },
    monthsBarStyle() {
      return { width: `calc(${ this.dates.length } * var(--col-px))` }
    },
    availabilityMap() {
      const map = {}

      this.availabilities.forEach(item => {
        const inner = {}

        item.availability.forEach(a => {
          inner[a.date] = a.count
        })
        map[item.id] = inner
      })

      return map
    },
    todayIso() {
      return format(new Date(), 'yyyy-MM-dd')
    },
    isHotelReception() {
      return this.$store.state.userGroups.map(g => g.code).includes('hotel_reception')
    },
    isGeneralManager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    isHotelReservations() {
      return this.$store.state.userGroups.map(item => item.code).includes('hotel_reservations')
    },
    isSalesManager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    userAppData() {
      return this.$store.state.userData
    },
    datePickerKey() {
      return `dp_${ format(this.baseDate, 'yyyy-MM') }`
    },
    datePickerAttrs() {
      return this.baseDate ? [{ key: 'block-current', dates: this.baseDate, contentClass: 'hm-vc-blocked' }] : []
    },
  },
  async created() {
    this.setPeriodFromAnchor(this.baseDate)
    await this.pickInitialProperty()
    if (this.canSubscribe) {
      getAvailabilitySubscriptions({ active: true, page_size: 1 }).then(({ data }) => { this.subscriptionCount = data.count }).catch(() => {})
    }
    await this.reloadPeriod()
  },
  mounted() {
    const el = this.$refs.cal
    if (el) {
      el.addEventListener('dragscroll-end', e => {
        const moved = e && e.detail && e.detail.moved
        this.dragJustMoved = !!moved
        if (this.dragJustMoved) requestAnimationFrame(() => (
          this.dragJustMoved = false
        ))
      })
    }
    document.addEventListener('click', this.onDocClick, true)
    document.addEventListener('keydown', this.onEscClose)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick, true)
    document.removeEventListener('keydown', this.onEscClose)
  },
  methods: {
    openSubscription(roomType = null, checkIn = null) {
      const selectedDates = this.selectedColIdxs.map(index => this.dates[index]?.iso).filter(Boolean).sort()
      const firstNight = selectedDates[0] || checkIn
      const lastNight = selectedDates[selectedDates.length - 1] || checkIn
      this.$router.push({
        name: 'hotel-management-availability-subscriptions',
        query: {
          create: '1',
          property: this.propertyCode,
          ...(roomType ? { room_type: String(roomType) } : {}),
          ...(firstNight ? { check_in: firstNight, check_out: format(addDays(parseISO(lastNight), 1), 'yyyy-MM-dd') } : {}),
        },
      })
    },
    async loadCore(apiPeriod) {
      this.isCoreLoading = true
      try {
        const rt = await getHotelManagementCalendarRoomTypes(this.propertyCode)
        this.roomTypes = rt?.data || []

        const [blocks, reservations, sharedRooms] = await Promise.all([
          getHotelManagementCalendarBlocks(apiPeriod),
          getHotelManagementCalendarReservationRooms(apiPeriod),
          getHotelManagementCalendarSharedRooms(apiPeriod),
        ])

        this.blocks = blocks.data
        this.reservations = reservations.data
        this.sharedRooms = sharedRooms.data
        this.hasLoadedOnce = true
      } finally {
        this.isCoreLoading = false
      }
    },
    async loadStats(apiPeriod) {
      this.isStatsLoading = true
      this.isOccupancyLoading = true
      this.isAvailabilityLoading = true
      try {
        const occP = getHotelManagementCalendarOccupancy(apiPeriod)
          .then(res => {
            this.occupancy = res.data
          })
          .finally(() => {
            this.isOccupancyLoading = false
          })
        const availP = getHotelManagementCalendarRoomTypeAvailability(apiPeriod)
          .then(res => {
            this.availabilities = res.data
          })
          .finally(() => {
            this.isAvailabilityLoading = false
          })
        await Promise.allSettled([occP, availP])
      } finally {
        this.isStatsLoading = false
      }
    },
    async loadUnassigned(apiPeriod) {
      this.isUnassignedLoading = true
      try {
        const { data } = await getHotelManagementReservationsRooms({ ...apiPeriod, unassigned: true })
        this.unassignedRooms = Array.isArray(data) ? data : []
      } catch (e) {
        this.unassignedRooms = []
      } finally {
        this.isUnassignedLoading = false
      }
    },
    anchorFromPeriod() {
      if (this.period && this.period.from_date) return addDays(parseISO(this.period.from_date), 2)
      return new Date()
    },
    sharedStatusToColor(code) {
      switch (code) {
      case 'checked_in':
        return 'var(--tone-green)'
      case 'checked_out':
        return 'var(--tone-gray)'
      case 'not_checked_in':
      default:
        return 'var(--tone-blue)'
      }
    },
    getSharedBackground(b) {
      if (b._source !== 'shared') return null
      const arr = Array.isArray(b.status) ? b.status : []
      const s1 = arr[0] || 'not_checked_in'
      const s2 = arr[1] || s1
      const c1 = this.sharedStatusToColor(s1)
      const c2 = this.sharedStatusToColor(s2)
      if (c1 === c2) return c1
      return `linear-gradient(to right, ${ c1 } 0 50%, ${ c2 } 50% 100%)`
    },
    onUnassignedRoomPatched() {
      this.$modal.hide('unassigned-rooms')
      this.reloadCalendarData()
    },
    onOpenUnassignedRoomsModal() {
      this.$modal.show('unassigned-rooms')
    },
    groupIdByRoom(roomId) {
      return this.roomIdToGroupId[roomId] ?? null
    },
    canDrag(b) {
      if (!b) return false
      if (b._source === 'reservation') return !b.lock_assignment
      if (b._source === 'block') return this.isGeneralManager
      return false
    },
    buildAllowedMap(b) {
      const allowed = {}
      const s = b._startIdx
      const e = b._endIdx
      const srcGroup = this.groupIdByRoom(b.room_id)

      const hasCollision = (roomId) => {
        const list = this.bookingsByRoom[roomId]

        return list.some(x => {
          if (x.id === b.id && x._source === b._source) return false
          return !(x._endIdx <= s || x._startIdx >= e)
        })
      }

      let allowedGroupIds = null
      if (b._source === 'reservation') {
        const catId = b.room_category_id
        const fromMap = catId && this.categoryAllowedGroups ? this.categoryAllowedGroups[catId] : null

        if (fromMap && fromMap.size > 0) {
          allowedGroupIds = fromMap
        } else {
          allowedGroupIds = new Set([srcGroup])
        }
      }

      this.roomTypes.forEach(g => {
        g.rooms.forEach(r => {
          if (b._source === 'block') {
            allowed[r.id] = this.isGeneralManager && !hasCollision(r.id)
          } else if (b._source === 'reservation') {
            const groupOk = allowedGroupIds ? allowedGroupIds.has(g.id) : (g.id === srcGroup)
            allowed[r.id] = groupOk && !hasCollision(r.id)
          } else {
            allowed[r.id] = false
          }
        })
      })

      return allowed
    },
    onBookingDragStart(b, e) {
      if (!this.canDrag(b)) return
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        try { e.dataTransfer.setData('text/plain', String(b.id)) } catch {}
      }
      this.popoverShow = false
      this.popoverSharedShow = false
      this.dnd.dragging = {
        id: b.id,
        room_id: b.room_id,
        _startIdx: b._startIdx,
        _endIdx: b._endIdx,
        _source: b._source,
        cutLeft: this.isCutLeft(b),
        cutRight: this.isCutRight(b),
      }
      this.dnd.allowedByRoom = this.buildAllowedMap(b)
      this.dnd.overRoomId = null
    },
    onBookingDragEnd() {
      this.dnd.dragging = null
      this.dnd.allowedByRoom = {}
      this.dnd.overRoomId = null
    },
    async loadRoomCategories() {
      try {
        const { data } = await getHotelManagementRoomCategories({ property: this.propertyCode })
        this.roomCategories = data

        const map = {}
        this.roomCategories.forEach(cat => {
          map[cat.id] = new Set(cat.room_types)
        })
        this.categoryAllowedGroups = map
      } catch (e) {
        this.roomCategories = []
        this.categoryAllowedGroups = {}
      }
    },
    onRowDragEnter(roomId) {
      if (this.dnd.dragging) this.dnd.overRoomId = roomId
    },
    onRowDragOver(roomId, e) {
      if (!this.dnd.dragging) return
      if (e.dataTransfer) e.dataTransfer.dropEffect = this.canDropTo(roomId) ? 'move' : 'none'
    },
    onRowDragLeave(roomId) {
      if (this.dnd.overRoomId === roomId) this.dnd.overRoomId = null
    },
    onRowDrop(roomId, e) {
      const drag = this.dnd.dragging
      if (!drag) return
      if (!this.canDropTo(roomId) || drag.room_id === roomId || !this.canDrag(drag)) return

      if (drag._source === 'block') {
        this.bookingLoadingKey = `b_block_${ drag.id }`
        patchHotelManagementBlocks(drag.id, { room: roomId })
          .then(() => this.reloadPeriod())
          .finally(() => {
            this.bookingLoadingKey = null
            this.onBookingDragEnd()
          })
      } else {
        this.bookingLoadingKey = `b_reservation_${ drag.id }`
        patchHotelManagementReservationsRooms(drag.id, { room: roomId })
          .then(() => this.reloadPeriod())
          .finally(() => {
            this.bookingLoadingKey = null
            this.onBookingDragEnd()
          })
      }
    },
    canDropTo(roomId) {
      const drag = this.dnd.dragging
      if (!drag) return false
      if (drag.room_id === roomId) return false
      return !!(this.dnd.allowedByRoom && this.dnd.allowedByRoom[roomId])
    },
    cellDropStyle(roomId, idx) {
      const drag = this.dnd.dragging
      if (!drag || !this.canDropTo(roomId)) return null

      const s = drag._startIdx, e = drag._endIdx
      const inRangeInclusive = idx >= s && idx <= e
      if (!inRangeInclusive) return null

      const color = '#eef8f0'
      const isStart = !drag.cutLeft && idx === s
      const isEnd = !drag.cutRight && idx === e
      const isMid = (idx > s && idx < e) || (drag.cutLeft && idx === s) || (drag.cutRight && idx === e)

      if (isMid) return { background: color }
      if (isStart) return { background: `linear-gradient(to right, transparent 0 50%, ${ color } 50% 100%)` }
      if (isEnd) return { background: `linear-gradient(to right, ${ color } 0 50%, transparent 50% 100%)` }

      return null
    },
    onChangeRoom() {
      this.isSubmittingChangeRoom = true
      patchHotelManagementReservationsRooms(this.current_room.id, { room: this.room_to_change })
        .finally(() => (this.isSubmittingChangeRoom = false))
        .then(() => {
          this.$modal.hide('change-room')
          this.reloadCalendarData()
        })
    },
    async onUnassign(room) {
      if (!room) return
      this.btnLoading.unassign = true
      try {
        await patchHotelManagementReservationsRooms(room.id, { room: null })
        await this.reloadCalendarData()
      } finally {
        this.btnLoading.unassign = false
      }
    },
    async onUnlock(room) {
      if (!room) return
      this.btnLoading.unlock = true
      try {
        await patchHotelManagementReservationsRooms(room.id, { lock_assignment: false })
        await this.reloadCalendarData()
      } finally {
        this.btnLoading.unlock = false
      }
    },
    async onLock(room) {
      if (!room) return
      this.btnLoading.lock = true
      try {
        await patchHotelManagementReservationsRooms(room.id, { lock_assignment: true })
        await this.reloadCalendarData()
      } finally {
        this.btnLoading.lock = false
      }
    },
    onChange(room) {
      this.current_room = { ...room }
      getHotelManagementRoomsAvailable({
        from_date: room.short_room.check_in,
        to_date: room.short_room.check_out,
        room_category: room.short_room.room_category.id,
        property: room.reservation.property.code
      }).then(res => {
        this.rooms_available = res.data
        this.$modal.show('change-room')
      })
    },
    async reloadCalendarData() {
      if (!this.period.from_date || !this.period.to_date) {
        this.setPeriodFromAnchor(this.baseDate || new Date())
        await this.$nextTick()
      }
      try {
        this.popoverShow = false
        this.popoverSharedShow = false
        await this.reloadPeriod()
      } finally {
        this.isReloading = false
      }
    },
    async onSelectStatus(opt, roomId, currentCode) {
      if (!opt || !roomId || opt.code === currentCode) return
      try {
        this.popoverShow = false
        this.popoverSharedShow = false
        await patchHotelManagementReservationsRooms(roomId, { status: opt.code })
        await this.reloadPeriod()
      } catch (e) {}
    },
    isBookingLoading(b) {
      return this.bookingLoadingKey === `b_${ b._source }_${ b.id }`
    },
    formatDate(date) {
      if (!date) return ''
      return format(parseISO(date), 'dd MMM')
    },
    toggleCreateMenu() {
      this.isCreateMenuOpen = !this.isCreateMenuOpen
    },
    onCreateNewReservation() {
      this.isCreateMenuOpen = false
      this.$emit('create-new-reservation', { property: this.propertyObject, from_date: this.period.from_date })
    },
    onBlockDates() {
      this.isCreateMenuOpen = false
      this.$emit('block-dates', { property: this.propertyObject, from_date: this.period.from_date })
    },
    async pickInitialProperty() {
      if (this.isHotelReception) {
        const hp = this.userAppData && this.userAppData.hotel_property
        if (hp) {
          this.period.property = typeof hp === 'string' ? hp : hp.code
          return
        }
      }
      try {
        const res = await getHotelManagementPropertiesV2('')
        const list = res?.data || []

        this.propertiesOptions = list
        if (!this.propertyCode && list.length) {
          this.period.property = list[0].code
        } else if (list.length) {
          const inList = list.some(p => p.code === this.propertyCode)
          if (!inList) this.period.property = list[0].code
        }
      } catch (e) {}
    },
    formatOcc(val) {
      if (val === null) return ''
      return `${ new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(val) }%`
    },
    async loadRoomTypes() {
      try {
        const res = await getHotelManagementCalendarRoomTypes(this.propertyCode)
        this.roomTypes = res.data
      } catch (e) {}
    },
    togglePropList() {
      if (!this.isHotelReception) this.isPropListOpen = !this.isPropListOpen
    },
    async onSelectProperty(opt) {
      if (!opt || opt.code === this.period.property) {
        this.isPropListOpen = false
        return
      }
      this.period.property = opt.code
      this.isPropListOpen = false
      await this.loadRoomTypes()
      await this.reloadPeriod()
    },
    onDocClick(e) {
      if (this.isDatePickerOpen) {
        const pop = this.$refs.datePopover
        const btn = this.$el.querySelector('.hc-ctrl--icon')

        if (pop && !pop.contains(e.target) && btn && !btn.contains(e.target)) this.isDatePickerOpen = false
      }
      if (this.isPropListOpen) {
        const box = this.$refs.propSwitcher

        if (box && !box.contains(e.target)) this.isPropListOpen = false
      }
      if (this.isCreateMenuOpen) {
        const pop = this.$refs.createPopover
        const btn = this.$el.querySelector('.hc-ctrl--plus')

        if (pop && !pop.contains(e.target) && btn && !btn.contains(e.target)) this.isCreateMenuOpen = false
      }
      if (this.popoverBlockShow) {
        const pop = this.$refs.popover
        if (pop && !pop.contains(e.target)) this.popoverBlockShow = false
      }
    },
    onEscClose(e) {
      if (e.key === 'Escape') {
        this.isDatePickerOpen = false
        this.isPropListOpen = false
        this.isCreateMenuOpen = false
        this.popoverBlockShow = false
      }
    },
    toggleDatePicker() {
      const willOpen = !this.isDatePickerOpen
      if (willOpen) {
        const anchor = this.lastPickedDate || this.baseDate || this.anchorFromPeriod()
        this.baseDate = anchor
      }
      this.isDatePickerOpen = willOpen
    },
    async ensureDateVisible(dateObj, n = 3) {
      this.selectedColIdxs = []
      const iso = format(dateObj, 'yyyy-MM-dd')
      const idx = this.dates.findIndex(d => d.iso === iso)

      if (idx !== -1) {
        this.scrollToCol(idx, n)
        return
      }
      this.baseDate = dateObj
      this.setPeriodFromAnchor(dateObj)
      await this.$nextTick()

      const newIdx = this.dates.findIndex(d => d.iso === iso)
      if (newIdx !== -1) this.scrollToCol(newIdx, n)
      await this.reloadPeriod()
    },
    isSameDay(a, b) {
      if (!a || !b) return false
      return format(a, 'yyyy-MM-dd') === format(b, 'yyyy-MM-dd')
    },
    async onDatePicked(val) {
      const picked = val instanceof Date ? val : (val && val.date instanceof Date ? val.date : null)
      if (!picked) return
      if (this.isSameDay(picked, this.baseDate)) return

      this.lastPickedDate = picked
      this.baseDate = picked
      this.isDatePickerOpen = false
      await this.ensureDateVisible(picked, 3)
    },
    async onToday() {
      const today = new Date()

      this.lastPickedDate = null
      this.baseDate = today
      await this.ensureDateVisible(today, 3)
    },
    setPeriodFromAnchor(anchor) {
      const from = format(subDays(anchor, 2), 'yyyy-MM-dd')
      const to = format(addDays(parseISO(from), 29), 'yyyy-MM-dd')

      this.period.from_date = from
      this.period.to_date = to
    },
    async reloadPeriod() {
      const apiPeriod = { ...this.period, property: this.propertyCode }

      await this.loadCore(apiPeriod)
      await this.loadStats(apiPeriod)
      await this.loadUnassigned(apiPeriod)
      await this.loadRoomCategories()
    },
    onDateClick(idx) {
      if (!this.dragJustMoved) this.toggleCol(idx)
    },
    toggleCol(idx) {
      const list = this.selectedColIdxs || []
      const i = list.indexOf(idx)

      if (i === -1) this.selectedColIdxs = [...list, idx]
      else {
        const copy = list.slice()
        copy.splice(i, 1)
        this.selectedColIdxs = copy
      }
    },
    toggleRow(roomId) {
      const list = this.selectedRoomIds || []
      const i = list.indexOf(roomId)

      if (i === -1) this.selectedRoomIds = [...list, roomId]
      else {
        const copy = list.slice()
        copy.splice(i, 1)
        this.selectedRoomIds = copy
      }
    },
    isColSelected(idx) {
      return this.selectedColIdxs.indexOf(idx) !== -1
    },
    isRowSelected(roomId) {
      return this.selectedRoomIds.indexOf(roomId) !== -1
    },
    isToday(iso) {
      return iso === this.todayIso
    },
    monthItemStyle(m) {
      return { left: `${ m.startIdx * COL_PX }px`, width: `${ m.days * COL_PX }px` }
    },
    getAvailability(groupId, iso) {
      const m = this.availabilityMap[groupId]
      return m && m[iso] !== null ? m[iso] : ''
    },
    bookingStyle(b) {
      const H = ROW_PX - (BOOKING_VPAD * 2)
      const MID = COL_PX / 2
      const GUTTER = 1
      const totalPx = this.dates.length * COL_PX
      const startPx = b._startIdx * COL_PX
      const endPx = b._endIdx * COL_PX
      const cutLeft = this.isCutLeft(b)
      const cutRight = this.isCutRight(b)
      const leftCutPx = cutLeft ? 0 : MID
      const rightCutPx = cutRight ? 0 : MID
      let left = startPx + leftCutPx
      let right = endPx + rightCutPx

      left = Math.max(0, left)
      right = Math.min(totalPx, right)

      let width = Math.max(0, right - left)
      const halfGap = GAP_PX / 2
      const marginL = cutLeft ? 0 : halfGap
      const marginR = cutRight ? 0 : halfGap

      left += (GUTTER / 2) + marginL
      width = Math.max(0, width - GUTTER - marginL - marginR)

      const style = {
        transform: `translate3d(${ left }px, 0, 0)`,
        width: `${ width }px`,
        height: `${ H }px`,
        lineHeight: `${ H }px`,
        top: `${ BOOKING_VPAD }px`
      }

      if (b._source === 'shared') {
        const bg = this.getSharedBackground(b)
        if (bg) style.background = bg
      }

      return style
    },
    isCutLeft(b) {
      return differenceInCalendarDays(parseISO(b.from_date), parseISO(this.period.from_date)) < 0
    },
    isCutRight(b) {
      const toIdx = differenceInCalendarDays(parseISO(b.to_date), parseISO(this.period.from_date))
      return toIdx > this.dates.length - 1
    },
    bookingToneClasses(b) {
      if (b._source === 'shared') return TONES.blue
      if (b.vacant && b.status === 'not_checked_in') return TONES.yellow
      if (b._source === 'block') return b.status === 'unavailable' ? TONES.red : TONES.black
      if (b.reservation_status === 'booked_provisionally') return TONES.turq
      if (b.reservation_status === 'booked') {
        if (b.status === 'checked_in') return TONES.green
        if (b.status === 'checked_out') return TONES.gray
        if (b.status === 'no_show') return TONES.burgundy
        return TONES.default
      }
      return TONES.default
    },
    onBookingClick(b, evt) {
      const el = (evt && evt.currentTarget) || (evt && evt.target && evt.target.closest && evt.target.closest('.hc-booking')) || null
      this.anchorEl = el
      try { this.anchorRect = el ? el.getBoundingClientRect() : null } catch (e) { this.anchorRect = null }

      this.popoverShow = false
      this.popoverSharedShow = false
      this.popoverBlockShow = false

      this.bookingLoadingKey = `b_${ b._source }_${ b.id }`

      if (b._source === 'shared') {
        getHotelManagementReservationShareableRoomsList(b.id)
          .then(res => {
            this.popoverData = res.data
            this.popoverSharedShow = true
            this.$nextTick(() => this.positionPopoverNear())
          })
          .finally(() => {
            this.bookingLoadingKey = null
          })
        return
      }

      if (b._source === 'block') {
        getHotelManagementBlock(b.id)
          .then(res => {
            this.popoverData = res.data
            this.popoverBlockShow = true
            this.$nextTick(() => this.positionPopoverNear())
          })
          .finally(() => {
            this.bookingLoadingKey = null
          })
        return
      }

      getHotelManagementReservationsRoom(b.id)
        .then(res => {
          this.popoverData = res.data
          this.popoverShow = true
          this.$nextTick(() => this.positionPopoverNear())
        })
        .finally(() => {
          this.bookingLoadingKey = null
        })
    },
    async onDeleteBlock() {
      const d = this.popoverData || {}
      if (!d || !d.id) return
      this.isSubmittingDeleteBlock = true
      try {
        await deleteHotelManagementBlock(d.id)
        this.popoverBlockShow = false
        await this.reloadCalendarData()
      } finally {
        this.isSubmittingDeleteBlock = false
      }
    },
    positionPopoverNear() {
      const pop = this.$refs.popover
      let ar = null

      if (this.anchorEl && this.anchorEl.getBoundingClientRect) {
        try {
          ar = this.anchorEl.getBoundingClientRect()
        } catch (e) { ar = null }
      }
      if (!ar || (!ar.width && !ar.height)) ar = this.anchorRect
      if (!pop || !ar) {
        this.centerPopoverFallback()
        return
      }

      const vw = window.innerWidth
      const vh = window.innerHeight
      const pw = pop.offsetWidth
      const ph = pop.offsetHeight
      const pad = 12
      const gap = 8
      let top = ar.bottom + gap
      let side = 'top'

      if (top + ph + pad > vh) {
        top = ar.top - ph - gap
        side = 'bottom'
      }
      if (top < pad) top = pad
      if (top + ph + pad > vh) top = Math.max(pad, vh - ph - pad)

      let left = ar.left + (ar.width / 2) - (pw / 2)

      if (left < pad) left = pad
      if (left + pw + pad > vw) left = Math.max(pad, vw - pw - pad)

      const anchorCenterX = ar.left + (ar.width / 2)
      const tailPad = 14
      let tailLeft = anchorCenterX - left

      tailLeft = Math.max(tailPad, Math.min(pw - tailPad, tailLeft))

      this.popoverPos = { top, left }
      this.tailSide = side === 'top' ? 'top' : 'bottom'
      this.tailLeft = Math.round(tailLeft)
    },
    centerPopoverFallback() {
      const pop = this.$refs.popover
      const vw = window.innerWidth
      const vh = window.innerHeight
      const pw = pop.offsetWidth
      const ph = pop.offsetHeight
      const pos = {
        top: Math.max(12, (vh - ph) / 2),
        left: Math.max(12, (vw - pw) / 2)
      }

      this.popoverPos = pos
      this.tailSide = 'top'
      this.tailLeft = Math.round(pw / 2)
    },
    scrollToCol(idx, n = 3) {
      const cal = this.$refs.cal
      if (!cal) return

      const colsBefore = Math.max(0, n - 1)
      const colLeft = idx * COL_PX
      const offsetPx = colsBefore * COL_PX
      const target = colLeft - offsetPx
      const maxScroll = Math.max(0, cal.scrollWidth - cal.clientWidth)
      const next = Math.min(Math.max(0, target), maxScroll)

      if (typeof cal.scrollTo === 'function') cal.scrollTo({ left: next, behavior: 'smooth' })
      else cal.scrollLeft = next
    },
  },
  directives: {
    dragScroll: {
      inserted(el) {
        el.classList.add('drag-scroll')
        let isDown = false, startX = 0, startY = 0, startLeft = 0, startTop = 0, moved = false
        const point = e => (
          'touches' in e ? e.touches[0] : e
        )
        const isAllowedStart = e => {
          const t = e.target
          if (!t) return false
          return !!(
            t.closest('.hc-dates') || t.closest('.hc-availability')
          )
        }
        const onDown = e => {
          if (e.button !== null && e.button !== 0) return
          if (!isAllowedStart(e)) return
          const p = point(e)
          isDown = true
          moved = false
          startX = p.pageX
          startY = p.pageY
          startLeft = el.scrollLeft
          startTop = el.scrollTop
          el.classList.add('is-grabbing')
          el.style.userSelect = 'none'
        }
        let rafId = null, lastDx = 0, lastDy = 0
        const onMove = e => {
          if (!isDown) return
          const p = point(e)
          const dx = p.pageX - startX, dy = p.pageY - startY
          if (!moved && (
            Math.abs(dx) > 3 || Math.abs(dy) > 3
          )) moved = true
          lastDx = dx
          lastDy = dy
          if (rafId === null) {
            rafId = requestAnimationFrame(() => {
              el.scrollLeft = startLeft - lastDx
              el.scrollTop = startTop - lastDy
              rafId = null
            })
          }
          if (e.cancelable) e.preventDefault()
        }
        const onUp = () => {
          if (!isDown) return
          isDown = false
          el.classList.remove('is-grabbing')
          el.style.userSelect = ''
          el.dispatchEvent(new CustomEvent('dragscroll-end', { detail: { moved } }))
          if (rafId) {
            cancelAnimationFrame(rafId)
            rafId = null
          }
        }
        el.addEventListener('mousedown', onDown, { passive: true })
        el.addEventListener('mousemove', onMove, { passive: false })
        window.addEventListener('mouseup', onUp)
        el.addEventListener('touchstart', onDown, { passive: true })
        el.addEventListener('touchmove', onMove, { passive: false })
        window.addEventListener('touchend', onUp)
        window.addEventListener('touchcancel', onUp)
        el._dragScroll = { onDown, onMove, onUp }
      },
      unbind(el) {
        const h = el._dragScroll
        if (!h) return
        el.removeEventListener('mousedown', h.onDown)
        el.removeEventListener('mousemove', h.onMove)
        window.removeEventListener('mouseup', h.onUp)
        el.removeEventListener('touchstart', h.onDown)
        el.removeEventListener('touchmove', h.onMove)
        window.removeEventListener('touchend', h.onUp)
        window.removeEventListener('touchcancel', h.onUp)
        delete el._dragScroll
      }
    }
  }
}
</script>

<style scoped>
.h-calendar {
  --tone-default: #70c2e1;
  --tone-blue: #3b97c5;
  --tone-turq: #439ba2;
  --tone-green: #7dbd4e;
  --tone-black: #282828;
  --tone-gray: #a3a3a3;
  --tone-red: #e73a00;
  --tone-yellow: #e7a200;
  --tone-burgundy: #b64f4f;

  --col-px: 100px;
  --row-px: 38px;
  --app-sidebar-w: 235px;
  --booking-vpad: 4px;
  width: calc(100vw - var(--app-sidebar-w-responsive, var(--app-sidebar-w)));
  overflow-x: visible;
  overflow-y: visible;
  font-family: 'MazzardSoftH', sans-serif;
  font-weight: 600;
}

@media (max-width: 1675px) {
  .h-calendar {
    --app-sidebar-w-responsive: 200px;
  }
}

.h-calendar__wrapper {
  position: relative;
  display: flex;
}

.h-calendar__top {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  border-bottom: 1px solid #e6e6e6;
  padding: 10px 20px;
  background-color: #fcfcfc;
}

.hc-switcher__current {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  height: 25px;
  font-family: 'MazzardSoftH', sans-serif;
  color: #0f172a;
  font-size: 17px;
  font-weight: 600;
  line-height: 36px;
  cursor: pointer;
}

.hc-switcher__current:disabled {
  opacity: .6;
  cursor: default;
  pointer-events: none;
}

.hc-switcher__name {
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hc-switcher__caret {
  width: 6px;
  height: 6px;
  margin-top: -2px;
  border-right: 2px solid #6b7280;
  border-bottom: 2px solid #6b7280;
  transform: rotate(45deg);
  transition: transform .15s ease;
}

.hc-switcher__caret.is-open {
  transform: rotate(-135deg);
}

.hc-switcher__popover {
  position: absolute;
  margin-top: 8px;
  min-width: 260px;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, .12);
  padding: 10px 8px;
}

.hc-switcher__label {
  padding: 6px 10px 8px;
  font-size: 12px;
  color: #9aa0a6;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.hc-switcher__list {
  list-style: none;
  margin: 0;
  padding: 4px;
}

.hc-switcher__item {
  margin: 2px 0;
}

.hc-switcher__btn {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 16px;
  color: #0f172a;
  cursor: pointer;
}

.hc-switcher__btn:hover {
  background: #f1f5f9;
}

.hc-switcher__item[aria-selected="true"] .hc-switcher__btn,
.hc-switcher__btn[disabled],
.hc-switcher__btn[disabled]:hover {
  background: #f1f5f9;
  cursor: default;
}

.h-calendar__sidebar {
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 20;
  width: 220px;
}

.h-calendar__body {
  position: relative;
  min-height: 600px;
  overflow-x: auto;
}

.hc-plug {
  height: 38px;
  border-bottom: 1px solid #e6e6e6;
  padding: 10px 20px;
  background-color: #f9f9f9;
}

.hc-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 84px;
  padding: 12px 12px;
  background-color: #fff;
}

.hc-ctrl {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 0;
  cursor: pointer;
  user-select: none;
  transition: filter 0.3s;
}

.hc-ctrl:hover {
  filter: contrast(0.95);
}

.hc-ctrl:disabled {
  opacity: .5;
  cursor: default
}

.hc-ctrl--icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background-color: #ebebeb;
  border-radius: 50%;
  color: #6b7280;
}

.hc-ctrl--today {
  width: 100px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 80px;
  border: 1px solid #dee2e7;
  background: linear-gradient(180deg, #f0f2f6 0%, #f0f3f8 100%);
  color: #8d95a4;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
  line-height: 40px;
  font-weight: 500;
}

.hc-ctrl--plus {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background: transparent;
  border-radius: 50%;
  color: #3e8a48;
}

.hc-sidebar {
  border-right: 1px solid #e6e6e6
}

.hc-sidebar__group:first-child .hc-sidebar__title {
  height: 39px;
  border-top: 1px solid #e6e6e6;
}

.hc-sidebar__title {
  position: relative;
  height: 38px;
  padding: 10px 20px;
  border-bottom: 1px solid #e6e6e6;
  background-color: #f6f6f6;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hc-sidebar__title--subscribable {
  padding-right: 44px;
}

.hc-sidebar__room {
  height: 38px;
  padding: 10px 10px 10px 20px;
  border-bottom: 1px solid #e6e6e6;
  background: #fff;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hc-head {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  border-bottom: 1px solid #e6e6e6;
}

.hc-months {
  height: 38px;
  position: relative;
}

.hc-months__bar {
  position: relative;
  height: 100%;
  border-bottom: 1px solid #e6e6e6;
}

.hc-months__item {
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: #f9f9f9;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  border-right: 1px solid #e6e6e6;
}

.hc-dates {
  display: grid
}

.hc-dates__cell {
  position: relative;
  height: 84px;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.hc-dates__cell.has-unassigned {
  background: linear-gradient(180deg, #ffcc85 0%, #ffc67b 100%);
}

.hc-dates__badge {
  position: absolute;
  top: 6px;
  right: 6px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 50%;
  background-color: #ff8400;
  color: #fff;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 23px;
  text-align: center;
}

.hc-dates__badge--unset {
  position: unset;
}

.hc-dates__occ {
  margin-top: 10px;
  color: #181818;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 12px;
  font-weight: 400;
}

.hc-dates__dow {
  color: #5f5f5f;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase
}

.hc-dates__day {
  color: #181818;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 17px;
  font-weight: 700;
}

.hc-availability__grid {
  display: grid
}

.hc-availability__cell {
  position: relative;
  height: 38px;
  border-right: 1px solid #f0f0f0;
  border-bottom: 1px solid #e6e6e6;
  background: #f6f6f6;
  text-align: center;
  font-size: 12px;
  line-height: 38px;
  font-weight: 600;
}

.hc-availability__val {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hc-subscribe {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  padding: 0;
  font-size: 13px;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.hc-availability__val--zero {
  width: 16px;
  height: 16px;
  padding-top: 2px;
  border-radius: 3px;
  background: #e73a00;
  color: #fff;
  line-height: 16px;
  font-weight: 600;
}

.hc-row__grid {
  position: relative;
  display: grid
}

.hc-row__cell {
  height: var(--row-px);
  border-right: 1px solid #f0f0f0;
  border-bottom: 1px solid #e6e6e6;
  background: rgba(255, 255, 255, .85);
}

.hc-booking {
  position: absolute;
  top: var(--booking-vpad);
  height: calc(var(--row-px) - (var(--booking-vpad) * 2));
  line-height: calc(var(--row-px) - (var(--booking-vpad) * 2));
  color: #fff;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 2px;
  padding: 0 10px;
  opacity: 0.9;
  cursor: pointer;
  transition: opacity 0.3s;

  &:hover {
    opacity: 1;
  }
}

.hc-booking--tone-default {
  background-color: var(--tone-default);
}

.hc-booking--tone-turq {
  background-color: var(--tone-turq);
}

.hc-booking--tone-blue {
  background-color: var(--tone-blue);
}

.hc-booking--tone-green {
  background-color: var(--tone-green);
}

.hc-booking--tone-black {
  background-color: var(--tone-black);
}

.hc-booking--tone-gray {
  background-color: var(--tone-gray);
}

.hc-booking--tone-red {
  background-color: var(--tone-red);
}

.hc-booking--tone-yellow {
  background-color: var(--tone-yellow);
}

.hc-booking--tone-burgundy {
  background-color: var(--tone-burgundy);
}

.hc-booking--cut-left::before, .hc-booking--cut-right::after {
  content: '';
  position: absolute;
  top: 0;
  width: 8px;
  height: 100%;
  background: repeating-linear-gradient(45deg, hsla(0, 0%, 100%, .7), hsla(0, 0%, 100%, .7) 2px, transparent 0, transparent 4px);
  pointer-events: none;
}

.hc-booking--cut-left::before {
  left: 0;
}

.hc-booking--cut-right::after {
  right: 0;
}

.hc-booking__spinner {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, .55);
  border-top-color: #fff;
  animation: hc-spin .6s linear infinite;
  pointer-events: none;
}

.hc-booking__icons {
  position: absolute;
  top: 2px;
  right: 2px;
  display: inline-flex;
  gap: 5px;
  align-items: center;
  pointer-events: none;
}

.hc-refresh-mask {
  position: fixed;
  z-index: 15;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 200px;
  background: rgba(255, 255, 255, 0.35);
  inset: 0;
}

.hc-refresh-spinner {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 4px solid rgba(0, 0, 0, .12);
  border-top-color: #6ec1ff;
  animation: hc-spin .8s linear infinite;
}

.hc-dates__cell.is-today, .hc-availability__cell.is-today, .hc-row__cell.is-today {
  background-color: #e9f5f8;
}

.hc-dates__cell.is-selected, .hc-availability__cell.is-selected, .hc-row__cell.is-selected, .hc-sidebar__room.is-selected {
  background-color: #fbf9e3;
}

.hc-sidebar__header {
  position: relative
}

.hc-date-popover {
  position: absolute;
  top: 64px;
  left: 12px;
  z-index: 50;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, .12);
  padding: 8px;
}

.hc-date-popover :deep(.hm-vc-blocked) {
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.6;
}

.hc-date-popover .vc-container {
  border: none
}

@keyframes hc-spin {
  to {
    transform: rotate(360deg)
  }
}

@media (prefers-reduced-motion: reduce) {
  .hc-booking {
    transition: none
  }
}

.drag-scroll {
  cursor: default
}

.hc-dates, .hc-availability {
  cursor: grab
}

.is-grabbing {
  cursor: grabbing
}

.hc-create-popover {
  position: absolute;
  top: 64px;
  right: 12px;
  z-index: 50;
  border-radius: 16px;
  border: 1px solid #e9e9e9;
  background: #fff;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.06);
  padding: 10px 12px;
}

.hc-create {
  list-style: none;
  margin: 0;
  padding: 0;
}

.hc-create__item + .hc-create__item {
  margin-top: 6px;
}

.hc-create__btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: #091411;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
}

.hc-create__btn:hover, .hc-create__btn:focus-visible {
  background: linear-gradient(180deg, #eee 0%, #ededed 100%);
  outline: none;
}

.hc-create__icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.hc-btn-spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: #a3a3a3;
  animation: hc-spin .6s linear infinite;
}

.hc-btn-spinner--lock {
  border-top-color: #ffcdbd;
}

.hc-btn-spinner--unassign {
  border-top-color: #ffcb83;
}

.hc-btn-spinner--unlock {
  border-top-color: #dee5d1;
}

@keyframes hm-shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
}

.hc-oc-skeleton, .hc-av-skeleton {
  display: inline-block;
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
  background-size: 400px 100%;
  animation: hm-shimmer 1.2s linear infinite;
  border-radius: 4px;
}

.hc-oc-skeleton {
  width: 38px;
  height: 12px;
  margin-top: 10px;
}

.hc-av-skeleton {
  width: 18px;
  height: 14px;
  border-radius: 3px;
}

.hc-legend-button {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #719c65;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 13px;
  font-weight: 500;
}

.guest {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.guest__gender {
  width: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.guest__flag {
  flex-shrink: 0;
  width: 19px;
  height: 19px;
  border-radius: 50%;
  border: 1px solid #b5b5b5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background: #fff;
  overflow: hidden;
}

.guest__flag--empty {
  background: #efefef;
  border-color: #efefef;
}

.guest__name {
  color: #000;
  font-size: 15px;
  font-family: 'CoreSansCR', sans-serif;
  font-weight: 500;
}

</style>
